/**
 * Dashboard Overview Page
 * Main dashboard with KPI cards, activity feed, quick actions
 */

import { type ComponentType } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@platform/ui";
import { Skeleton } from "@platform/ui";
import { Button } from "@platform/ui";
import {
  LayoutGridIcon,
  FileEditIcon,
  DollarSignIcon,
  BotIcon,
  PlusIcon,
  BarChartIcon,
  TrendingUpIcon,
  TrendingDownIcon,
} from "lucide-react";
import { trpc } from "@/lib/trpc";

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / 60000);
  if (diffInMinutes < 1) return "just now";
  if (diffInMinutes < 60)
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
};

function KPICard({
  title,
  value,
  trend,
  icon: Icon,
  loading,
}: {
  title: string;
  value: string | number;
  trend?: number;
  icon: ComponentType<{ className?: string }>;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-[var(--color-text-secondary)]">
              {title}
            </p>
            {loading ? (
              <Skeleton className="h-10 w-24 mt-2" />
            ) : (
              <div className="mt-2">
                <p className="text-3xl font-bold text-[var(--color-text-primary)]">
                  {value}
                </p>
                {trend !== undefined && trend !== 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    {trend > 0 ? (
                      <>
                        <TrendingUpIcon className="h-4 w-4 text-[var(--color-success-500)]" />
                        <span className="text-sm text-[var(--color-success-500)]">
                          +{trend}%
                        </span>
                      </>
                    ) : (
                      <>
                        <TrendingDownIcon className="h-4 w-4 text-[var(--color-error-500)]" />
                        <span className="text-sm text-[var(--color-error-500)]">
                          {trend}%
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-3 rounded-lg bg-[var(--color-bg-secondary)]">
            <Icon className="h-8 w-8 text-[var(--color-primary-500)]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardOverviewPage() {
  const { user, loading: userLoading } = useAuth();
  const { data: overview, isLoading: overviewLoading } =
    trpc.dashboard.getOverview.useQuery();
  const { data: financial, isLoading: financialLoading } =
    trpc.dashboard.getFinancialSnapshot.useQuery();
  const { data: recentPosts, isLoading: postsLoading } =
    trpc.posts.list.useQuery({ limit: 5 });
  const kpiLoading = overviewLoading || financialLoading;
  const activitiesLoading = postsLoading;
  const kpiData = {
    totalApps: overview?.total_apps ?? 0,
    activePosts: 0,
    monthlyRevenue: financial?.revenue.mrr ?? 0,
    agentRuns: 0,
    trends: {
      apps: 0,
      posts: 0,
      revenue: financial?.revenue.arr_growth_percent ?? 0,
      agents: 0,
    },
  };
  const activities = (recentPosts?.posts ?? []).map((post, index) => ({
    id: index + 1,
    type: "post",
    message: `Post "${post.title}" - ${post.status}`,
    time: formatTimeAgo(new Date(post.created_at)),
  }));

  if (userLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-96 mt-2" />
        </div>
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const displayName = user?.email?.split("@")[0] || "there";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-[var(--color-text-primary)]">
          Welcome back, {displayName}
        </h1>
        <p className="mt-2 text-[var(--color-text-secondary)]">
          Here's what's happening with your content today
        </p>
      </div>
      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Apps"
          value={kpiData.totalApps}
          trend={kpiData.trends.apps}
          icon={LayoutGridIcon}
          loading={kpiLoading}
        />
        <KPICard
          title="Active Posts"
          value={kpiData.activePosts}
          trend={kpiData.trends.posts}
          icon={FileEditIcon}
          loading={kpiLoading}
        />
        <KPICard
          title="Monthly Revenue"
          value={`$${kpiData.monthlyRevenue.toFixed(2)}`}
          trend={kpiData.trends.revenue}
          icon={DollarSignIcon}
          loading={kpiLoading}
        />
        <KPICard
          title="Agent Runs"
          value={kpiData.agentRuns}
          trend={kpiData.trends.agents}
          icon={BotIcon}
          loading={kpiLoading}
        />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Your latest platform updates</CardDescription>
          </CardHeader>
          <CardContent>
            {activitiesLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-[var(--color-text-secondary)]">
                  No recent activity
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-4 pb-4 border-b border-[var(--color-border-subtle)] last:border-0 last:pb-0"
                  >
                    <div className="h-10 w-10 rounded-full bg-[var(--color-primary-500)] flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-semibold">
                        {activity.type[0]!.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[var(--color-text-primary)]">
                        {activity.message}
                      </p>
                      <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                        {activity.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="primary" className="w-full justify-start gap-2">
              <PlusIcon className="h-4 w-4" />
              Create App
            </Button>
            <Button variant="secondary" className="w-full justify-start gap-2">
              <FileEditIcon className="h-4 w-4" />
              New Post
            </Button>
            <Button variant="secondary" className="w-full justify-start gap-2">
              <BarChartIcon className="h-4 w-4" />
              View Analytics
            </Button>
            <Button variant="ghost" className="w-full justify-start gap-2">
              <BotIcon className="h-4 w-4" />
              Run Agent
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
